/**
 * The player's settings: one struct, one storage key, one change event.
 *
 * ## Why this is not just a bag of fields on `main.ts`
 *
 * Every one of these is read by two or three subsystems and written by two or three UI controls,
 * and the milestone that adds the settings menu is exactly the point where "a variable in
 * bootstrap" stops working. A single object with a single change event means a control does not
 * need to know who consumes what, and a consumer does not need to know which control moved.
 *
 * It is also the shape a profile has to be in: plain, serialisable, no class instances, no
 * references to renderer objects. `StorageService` writes it verbatim and a future account
 * implementation stores the same JSON (non-negotiable #7).
 *
 * ## Units at the boundary
 *
 * `units` is a *display* preference and nothing else. The simulation is SI everywhere
 * (CLAUDE.md); this decides what the instruments and the HUD write on the glass, and it is read
 * only by code that is formatting a string for a human.
 *
 * `updateFixed` / `updateFrame`: neither. Written on a UI action, read on change.
 */

import { Emitter } from '../core/events.js';
import { TIERS, type QualityMode } from '../core/quality.js';
import {
  STORAGE_KEYS,
  booleanField,
  enumField,
  isRecord,
  numberField,
  readJson,
  writeJson,
  type StorageService,
} from '../core/storage.js';

/** Which units the instruments and readouts are labelled in. */
export const UNIT_SYSTEMS = ['aviation', 'metric'] as const;
export type UnitSystem = (typeof UNIT_SYSTEMS)[number];

/** Which camera the session opens in and `1`–`5` select. */
export const CAMERA_MODES = ['cockpit', 'chase', 'flyby', 'orbit', 'free'] as const;
export type CameraMode = (typeof CAMERA_MODES)[number];

export interface Settings {
  // --- graphics ---------------------------------------------------------------------------
  /** `auto` lets the governor pick; anything else pins the tier. */
  qualityMode: QualityMode;
  /** Manual exposure trim, 1 = neutral. */
  exposureBias: number;
  /** Field of view for the external cameras [deg]. */
  fovDeg: number;
  /**
   * Field of view for the cockpit view [deg].
   *
   * Separate from {@link fovDeg} on purpose. The two answer different questions: outside the
   * aeroplane a wide field is a nicer picture, and inside it a wide field shrinks the instruments
   * to illegibility while making the aeroplane feel like it is flying faster than it is. Sharing
   * one number means every pilot has to compromise one of them.
   */
  cockpitFovDeg: number;

  // --- audio ------------------------------------------------------------------------------
  /** Master volume, 0…1. */
  masterVolume: number;
  /** The engine bus. */
  engineVolume: number;
  /** The airframe bus: wind noise, the pre-stall buffet, the wheels. */
  windVolume: number;
  /**
   * The environment bus: the ambient wind bed, rain, birdsong.
   *
   * Its own slider rather than sharing the wind one, because they are opposite kinds of sound:
   * the airframe layer is information a pilot flies by and the ambience is atmosphere. Someone
   * who wants the aeroplane loud and the world quiet is making a reasonable request, and with
   * one slider for both there is no way to grant it.
   */
  ambienceVolume: number;
  /** The weapons and UI buses. */
  effectsVolume: number;
  muted: boolean;

  // --- instruments and aids ------------------------------------------------------------------
  units: UnitSystem;
  /** Draw the collimated fighter HUD over whatever aircraft is being flown. */
  fighterHud: boolean;
  /** The auto-coordination assist. */
  coordinationAssist: boolean;
  /** Mouse-yoke mode. */
  mouseYoke: boolean;
  /** Camera the session opens in. */
  cameraMode: CameraMode;
  /** Show the always-on control hint strip. */
  helpFooter: boolean;
}

export const DEFAULT_SETTINGS: Readonly<Settings> = Object.freeze({
  qualityMode: 'auto',
  exposureBias: 1,
  fovDeg: 55,
  /*
   * Wider than it looks like it should be, and it is a flyability number rather than a taste one.
   * A cockpit has to show the horizon *and* the six-pack at once, and those are 20° apart: at 45°
   * the panel and the glareshield between them fill the frame and there is nowhere to put the
   * horizon. 60° puts the horizon in the upper third, the six-pack in the lower, and still leaves
   * a 3.1-inch instrument 124 px across at 1080p.
   */
  cockpitFovDeg: 60,
  masterVolume: 0.8,
  engineVolume: 0.9,
  windVolume: 0.7,
  ambienceVolume: 0.6,
  effectsVolume: 0.9,
  muted: false,
  units: 'aviation',
  fighterHud: false,
  coordinationAssist: true,
  mouseYoke: false,
  cameraMode: 'chase',
  helpFooter: true,
} satisfies Settings);

export interface SettingsEvents extends Record<string, unknown> {
  /** Something changed. `keys` names what, so a consumer can ignore the rest. */
  change: { settings: Readonly<Settings>; keys: readonly (keyof Settings)[] };
}

/**
 * Owns the settings, validates what comes back off disk, and tells everyone when they move.
 *
 * Saves are **debounced by the caller, not here** — `patch` writes immediately, because every
 * caller is a discrete UI action (a click, a keypress, a slider release) rather than a stream.
 * A dragged slider that writes per frame should call {@link patch} on release and
 * {@link preview} while it moves.
 */
export class SettingsStore {
  readonly events = new Emitter<SettingsEvents>();

  private readonly storage: StorageService;
  private current: Settings;

  constructor(storage: StorageService, initial: Partial<Settings> = {}) {
    this.storage = storage;
    this.current = { ...DEFAULT_SETTINGS, ...initial };
  }

  get settings(): Readonly<Settings> {
    return this.current;
  }

  /** Load whatever was stored. Anything invalid falls back to the default, field by field. */
  load(): void {
    const stored = readJson(this.storage, STORAGE_KEYS.settings, isRecord);
    if (stored === null) return;
    this.current = validate(stored, this.current);
    this.events.emit('change', { settings: this.current, keys: Object.keys(this.current) as (keyof Settings)[] });
  }

  /** Change some settings, tell everyone, and persist. */
  patch(changes: Partial<Settings>): void {
    const keys = this.preview(changes);
    if (keys.length > 0) this.save();
  }

  /**
   * Change some settings and tell everyone, *without* persisting.
   *
   * For a control that is being dragged: the scene should follow the slider, but writing to
   * storage sixty times a second is a waste and, on a quota-limited store, a way to fail the one
   * write that mattered. Call {@link patch} on release.
   */
  preview(changes: Partial<Settings>): (keyof Settings)[] {
    const changed: (keyof Settings)[] = [];
    for (const key of Object.keys(changes) as (keyof Settings)[]) {
      const value = changes[key];
      if (value === undefined || Object.is(this.current[key], value)) continue;
      // Each field is its own type; the record is checked key-by-key on the way in from storage
      // and by the caller's own types here, so one assignment cast beats fifteen branches.
      (this.current as unknown as Record<string, unknown>)[key] = value;
      changed.push(key);
    }
    if (changed.length > 0) this.events.emit('change', { settings: this.current, keys: changed });
    return changed;
  }

  /** Persist the current settings. Returns false when storage refused. */
  save(): boolean {
    return writeJson(this.storage, STORAGE_KEYS.settings, this.current);
  }

  /** Put everything back to the shipped defaults. */
  reset(): void {
    this.current = { ...DEFAULT_SETTINGS };
    this.events.emit('change', {
      settings: this.current,
      keys: Object.keys(this.current) as (keyof Settings)[],
    });
    this.save();
  }
}

/**
 * Narrow a stored record to `Settings`, field by field.
 *
 * Field by field rather than all-or-nothing, because the common reason a stored blob does not
 * match is that it was written by an older build that did not have one of these fields yet.
 * Throwing the whole thing away would silently reset a returning player's entire configuration
 * every time a setting is added, which is precisely the moment they would least expect it.
 */
function validate(record: Record<string, unknown>, fallback: Settings): Settings {
  const qualityModes: readonly QualityMode[] = ['auto', ...TIERS];
  return {
    qualityMode: enumField(record, 'qualityMode', qualityModes, fallback.qualityMode),
    exposureBias: numberField(record, 'exposureBias', fallback.exposureBias, 0.25, 4),
    fovDeg: numberField(record, 'fovDeg', fallback.fovDeg, 35, 100),
    cockpitFovDeg: numberField(record, 'cockpitFovDeg', fallback.cockpitFovDeg, 35, 100),
    masterVolume: numberField(record, 'masterVolume', fallback.masterVolume, 0, 1),
    engineVolume: numberField(record, 'engineVolume', fallback.engineVolume, 0, 1),
    windVolume: numberField(record, 'windVolume', fallback.windVolume, 0, 1),
    ambienceVolume: numberField(record, 'ambienceVolume', fallback.ambienceVolume, 0, 1),
    effectsVolume: numberField(record, 'effectsVolume', fallback.effectsVolume, 0, 1),
    muted: booleanField(record, 'muted', fallback.muted),
    units: enumField(record, 'units', UNIT_SYSTEMS, fallback.units),
    fighterHud: booleanField(record, 'fighterHud', fallback.fighterHud),
    coordinationAssist: booleanField(record, 'coordinationAssist', fallback.coordinationAssist),
    mouseYoke: booleanField(record, 'mouseYoke', fallback.mouseYoke),
    cameraMode: enumField(record, 'cameraMode', CAMERA_MODES, fallback.cameraMode),
    helpFooter: booleanField(record, 'helpFooter', fallback.helpFooter),
  };
}

/** Where the last session was flying, so the next one opens there. */
export interface LastFlight {
  aircraftId: string;
  icao: string;
  /** Which end of the runway, as the spawn picker names it. */
  runwayEnd: 'low' | 'high';
  /** Runway identifier, so a changed airport database does not put you on the wrong strip. */
  runwayIdent: string;
  mode: 'threshold' | 'final';
}

/** Read the last flight, or null if there was not one (or it no longer parses). */
export function loadLastFlight(storage: StorageService): LastFlight | null {
  const record = readJson(storage, STORAGE_KEYS.lastFlight, isRecord);
  if (record === null) return null;
  const aircraftId = record['aircraftId'];
  const icao = record['icao'];
  const runwayEnd = record['runwayEnd'];
  const runwayIdent = record['runwayIdent'];
  const mode = record['mode'];
  if (typeof aircraftId !== 'string' || typeof icao !== 'string') return null;
  if (typeof runwayIdent !== 'string') return null;
  return {
    aircraftId,
    icao,
    runwayEnd: runwayEnd === 'high' ? 'high' : 'low',
    runwayIdent,
    mode: mode === 'final' ? 'final' : 'threshold',
  };
}

export function saveLastFlight(storage: StorageService, flight: LastFlight): boolean {
  return writeJson(storage, STORAGE_KEYS.lastFlight, flight);
}
